import { uploadImage } from '@repo/shared-images-client-utils'
import { useCallback, useEffect, useState } from 'react'
import { insertImages, LinkBubbleMenu, RichTextContent, RichTextEditorProvider, TableBubbleMenu } from 'mui-tiptap'
import { EditorProps } from './types'
import useExtensions from './use-extensions'
import { EditorOptions, useEditor } from '@tiptap/react'
import { editorClasses } from './classes'
import { StyledRoot } from './styles'
import Stack from '@mui/material/Stack'
import Portal from '@mui/material/Portal'
import Backdrop from '@mui/material/Backdrop'
import FormHelperText from '@mui/material/FormHelperText'
import { EditorMenuControls } from './Toolbar'

export function MuiTipTapEditor({
  sx,
  error,
  onChange,
  slotProps,
  helperText,
  resetValue,
  className,
  editable = true,
  fullItem = false,
  simple = false,
  value: content = '',
  placeholder = 'Write something awesome...',
  ...other
}: EditorProps) {
  const [initialized, setInitialized] = useState(false)
  const [fullScreen, setFullScreen] = useState(false)

  /** Extensions **/
  const extensions = useExtensions()

  /** Editor **/
  const editor = useEditor({
    content,
    editable,
    immediatelyRender: false,
    // shouldRerenderOnTransaction: false,
    extensions: extensions,
    onUpdate({ editor: _editor }) {
      // If editor is empty, like if the results are just <p></p> then set content to empty string
      const isEmpty = _editor.getText() === '' && _editor.getHTML() === '<p></p>'

      const html = isEmpty ? '' : _editor.getHTML()
      onChange?.(html)
    },
    editorProps: {
      attributes: { spellCheck: 'false', autoComplete: 'off', autoCapitalize: 'off' }
    },
    ...other
  })

  const handleToggleFullScreen = useCallback(() => {
    setFullScreen(prev => !prev)
  }, [])

  const handleNewImageFiles = useCallback(
    async (files: File[], insertPosition?: number): Promise<void> => {
      console.log('handleNewImageFiles - files: ', files, ' | insertPosition: ', insertPosition)

      if (!editor) {
        console.log('handleNewImageFiles - !editor - returning')
        return
      }

      const attributesForImageFiles = []

      for (const file of files) {
        const uploadedImage = await uploadImage({ file: file })

        console.log('handleNewImageFiles - file: ', file, ' | uploadedImage: ', uploadedImage)

        attributesForImageFiles.push({
          src: uploadedImage.url,
          alt: file.name
        })
      }

      console.log('handleNewImageFiles - attributesForImageFiles: ', attributesForImageFiles)

      insertImages({
        images: attributesForImageFiles,
        editor: editor,
        position: insertPosition
      })
    },
    [editor]
  )

  /** Handle Drop **/
  const handleDrop: EditorOptions['editorProps']['handleDrop'] = useCallback(
    (view, event, _slice, _moved) => {
      console.log('handleDrop - view', view, 'event', event, '_slice', _slice, '_moved', _moved)

      //  If No DragEvent or DataTransfer or if this event is a move, Return Unhandled
      if (!(event instanceof DragEvent) || !event.dataTransfer || _moved === true) {
        return false
      }

      // Handle dropping images
      const images = Array.from(event?.dataTransfer?.files)?.filter(file => file?.type?.toLowerCase()?.startsWith('image'))

      if (images?.length > 0) {
        console.log('handleDrop - images', images)

        const insertPosition = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos

        handleNewImageFiles(images, insertPosition).then()

        event.preventDefault()
        return true
      }

      return false
    },
    [handleNewImageFiles]
  )

  /** Handle Paste **/
  const handlePaste: EditorOptions['editorProps']['handlePaste'] = useCallback(
    (view, event, slice) => {
      console.log('handlePaste - view: ', view, 'event: ', event, ' slice: ', slice)

      // If No Clipboard Data Return Unhandled
      if (!event.clipboardData) {
        return false
      }

      // Handle pasted images
      const pastedImageFiles = Array.from(event?.clipboardData?.files)?.filter(file => file?.type?.toLowerCase()?.startsWith('image'))

      if (pastedImageFiles?.length > 0) {
        console.log('handlePaste - pastedImageFiles', pastedImageFiles)

        handleNewImageFiles(pastedImageFiles).then()

        return true
      }

      return false
    },
    [handleNewImageFiles]
  )

  useEffect(() => {
    if (editor) {
      editor.setOptions({
        editorProps: {
          handleDrop: handleDrop,
          handlePaste: handlePaste
        }
      })

      window['nateEditor'] = editor
    }
  }, [editor, handleDrop, handlePaste])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (editor?.isEmpty && content !== '<p></p>') {
        editor.commands.setContent(content)
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [content, editor])

  useEffect(() => {
    if (resetValue && !content) {
      console.log('resetValue && !content - clearing content...')
      editor?.commands.clearContent()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content])

  useEffect(() => {
    if (fullScreen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }, [fullScreen])

  return (
    <Portal disablePortal={!fullScreen}>
      {fullScreen && <Backdrop open sx={{ zIndex: theme => theme.zIndex.modal - 1 }} />}

      <Stack sx={{ ...(!editable && { cursor: 'not-allowed' }), ...slotProps?.wrap }}>
        <StyledRoot error={!!error} disabled={!editable} fullScreen={fullScreen} className={editorClasses.root.concat(className ? ` ${className}` : '')} sx={sx}>
          <RichTextEditorProvider editor={editor}>
            <EditorMenuControls simple={simple} />
            <RichTextContent className={editorClasses.content.root} />
            <LinkBubbleMenu />
            <TableBubbleMenu />
          </RichTextEditorProvider>
        </StyledRoot>

        {helperText && (
          <FormHelperText error={!!error} sx={{ px: 2 }}>
            {helperText}
          </FormHelperText>
        )}
      </Stack>
    </Portal>
  )
}
